import { z } from 'zod';
import { languageSchema } from '../auth/index';
import { brandStyleRequestFields, brandStyleSettingsSchema, brandThemeFieldsSchema } from './brand';
import { loginDefaultSchema, orgTypeSchema } from './org-type';

export * from './brand';
export * from './master';
export * from './org-type';
export * from './org-type-profile';

/** `archived` (MST-2): opgeschort én uit de gewone lijst van Master; omkeerbaar. */
export const tenantStatusSchema = z.enum(['active', 'suspended', 'archived']);
export type TenantStatus = z.infer<typeof tenantStatusSchema>;

/**
 * De bedrijfscode staat in elk aanmeldscherm en is onwijzigbaar na aanmaak. Kleine
 * letters, cijfers en koppeltekens: makkelijk over te typen vanaf een briefje, en
 * hoofdletterongevoelig uniek.
 */
export const tenantCodeSchema = z
  .string()
  .min(3)
  .max(12)
  .transform((code) => code.trim().toLowerCase())
  .pipe(
    z
      .string()
      .regex(
        /^[a-z0-9][a-z0-9-]*[a-z0-9]$/,
        'Gebruik letters, cijfers en koppeltekens; begin en eindig met een letter of cijfer.',
      ),
  );

/**
 * IANA-tijdzone van het bedrijf, bijvoorbeeld `Europe/Brussels`.
 *
 * Een geplande uitdienst gaat in om middernacht — maar middernacht wáár? Voor een
 * productiebedrijf met een nachtploeg is dat geen detail: een uur te vroeg betekent dat
 * iemand midden in zijn shift wordt buitengesloten (ADR-0030).
 */
export const timezoneSchema = z
  .string()
  .min(3)
  .max(64)
  .regex(/^[A-Za-z]+\/[A-Za-z_+-]+(\/[A-Za-z_+-]+)?$/, 'Gebruik een IANA-tijdzone.');

export const tenantSchema = z.object({
  id: z.guid(),
  code: z.string(),
  name: z.string(),
  status: tenantStatusSchema,
  defaultLanguage: languageSchema,
  timezone: z.string(),
  logoFileId: z.guid().nullable(),
  orgType: orgTypeSchema,
});
export type Tenant = z.infer<typeof tenantSchema>;

/**
 * Soort bedrijf in de demo-omgeving (ACC-3): `shared` is Democorp algemeen, waar alle testers
 * samen testen; `personal` is Democorp <naam>, van één tester; `archived` is de oude stand na
 * terugzetten, opgeschort bewaard. `null` voor een gewone klant.
 */
export const tenantKindSchema = z.enum(['shared', 'personal', 'archived']);
export type TenantKind = z.infer<typeof tenantKindSchema>;

/** Wat een platformbeheerder over een bedrijf ziet in de lijst en op het detailscherm. */
export const tenantDetailSchema = tenantSchema.extend({
  suspendedAt: z.string().datetime().nullable(),
  suspendedReason: z.string().nullable(),
  createdAt: z.string().datetime(),
  kind: tenantKindSchema.nullable(),
  /** De tester van een persoonlijk bedrijf (ACC-3). */
  ownerEmail: z.string().nullable(),
  /** Het laatste domeinevent van dit bedrijf: wanneer er voor het laatst iets gebeurde. */
  lastActivityAt: z.string().datetime().nullable(),
});
export type TenantDetail = z.infer<typeof tenantDetailSchema>;

export const createTenantRequestSchema = z.object({
  code: tenantCodeSchema,
  name: z.string().min(2).max(120),
  defaultLanguage: languageSchema.default('nl'),
  timezone: timezoneSchema.default('Europe/Brussels'),
  kind: tenantKindSchema.nullable().default(null),
  ownerEmail: z.string().email().max(320).nullable().default(null),
  /** Zet bij het aanmaken meteen de vlaggen van het profiel (ORG-1). */
  orgType: orgTypeSchema.default('factory'),
});
export type CreateTenantRequest = z.infer<typeof createTenantRequestSchema>;

/** De code ontbreekt bewust: die ligt vast zodra medewerkers hem gebruiken om in te loggen. */
export const updateTenantRequestSchema = z.object({
  name: z.string().min(2).max(120).optional(),
  defaultLanguage: languageSchema.optional(),
  timezone: timezoneSchema.optional(),
  logoFileId: z.guid().nullable().optional(),
  kind: tenantKindSchema.nullable().optional(),
  ownerEmail: z.string().email().max(320).nullable().optional(),
});
export type UpdateTenantRequest = z.infer<typeof updateTenantRequestSchema>;

/**
 * Wat het beheer van een demobedrijf over zichzelf moet weten (ACC-3).
 *
 * `demoTools` is de vlag `tenants.demo_tools` én een soort: alleen dan bestaan "Vul met
 * demo-inhoud" en "Terugzetten naar beginstand". `canReset` alleen voor een persoonlijk bedrijf;
 * Democorp algemeen zet alleen de platformbeheerder terug.
 */
export const tenantDemoInfoSchema = z.object({
  name: z.string(),
  kind: tenantKindSchema.nullable(),
  demoTools: z.boolean(),
  canReset: z.boolean(),
});
export type TenantDemoInfo = z.infer<typeof tenantDemoInfoSchema>;

/** Terugzetten door de tester zelf: de bedrijfsnaam overtypen en het eigen wachtwoord. */
export const resetDemoTenantRequestSchema = z.object({
  confirmName: z.string().min(1).max(120),
  password: z.string().min(1).max(200),
});
export type ResetDemoTenantRequest = z.infer<typeof resetDemoTenantRequestSchema>;

/** Terugzetten door de platformbeheerder: alleen de bedrijfsnaam. */
export const platformResetDemoTenantRequestSchema = z.object({
  confirmName: z.string().min(1).max(120),
});
export type PlatformResetDemoTenantRequest = z.infer<typeof platformResetDemoTenantRequestSchema>;

export const resetDemoTenantResponseSchema = z.object({
  tenantId: z.guid(),
  code: z.string(),
  name: z.string(),
  /** Hoeveel mensen een mail kregen om hun toegang tot het nieuwe bedrijf te bevestigen. */
  invited: z.number().int(),
});
export type ResetDemoTenantResponse = z.infer<typeof resetDemoTenantResponseSchema>;

export const suspendTenantRequestSchema = z.object({
  reason: z.string().min(3).max(500),
});
export type SuspendTenantRequest = z.infer<typeof suspendTenantRequestSchema>;

export const tenantFeaturesResponseSchema = z.object({
  tenantId: z.guid(),
  features: z.record(z.string(), z.boolean()),
});
export type TenantFeaturesResponse = z.infer<typeof tenantFeaturesResponseSchema>;

/** Eén flag zoals de platformbeheerder hem ziet: waarde, herkomst en uitleg. */
export const tenantFeatureSchema = z.object({
  key: z.string(),
  enabled: z.boolean(),
  /**
   * 'tenant' = expliciet gezet voor dit bedrijf, 'platform' = de platformstandaard van Master
   * (MST-4), 'default' = standaard van de code of de omgeving.
   */
  source: z.enum(['tenant', 'platform', 'default']),
  defaultValue: z.boolean(),
  description: z.string(),
  owner: z.string(),
  module: z.string(),
});
export type TenantFeature = z.infer<typeof tenantFeatureSchema>;

export const tenantFeatureListSchema = z.object({
  tenantId: z.guid(),
  features: z.array(tenantFeatureSchema),
});
export type TenantFeatureList = z.infer<typeof tenantFeatureListSchema>;

/**
 * Het type van een organisatie wijzigen (ORG-1). Met `dryRun` zegt de server alleen wat er zou
 * veranderen, zodat het scherm dat eerst kan tonen.
 */
export const changeOrgTypeRequestSchema = z.object({
  orgType: orgTypeSchema,
  dryRun: z.boolean().default(false),
});
export type ChangeOrgTypeRequest = z.infer<typeof changeOrgTypeRequestSchema>;

/** Wat een wijziging van het type doet: welke vlaggen aan gaan en welke uit. */
export const orgTypeChangeSchema = z.object({
  orgType: orgTypeSchema,
  applied: z.boolean(),
  turnOn: z.array(z.string()),
  turnOff: z.array(z.string()),
});
export type OrgTypeChange = z.infer<typeof orgTypeChangeSchema>;

export const updateTenantFeaturesRequestSchema = z.object({
  /** Alleen de flags die je wil wijzigen; `null` verwijdert de override. */
  features: z.record(z.string(), z.boolean().nullable()),
});
export type UpdateTenantFeaturesRequest = z.infer<typeof updateTenantFeaturesRequestSchema>;

/**
 * Wat een medewerker ziet vóórdat hij is aangemeld: de naam van zijn bedrijf en het logo.
 *
 * Bewust karig. Dit is de enige weg naar bedrijfsgegevens zonder sessie, en ze bestaat om één
 * vraag te beantwoorden: "ben ik hier bij mijn eigen bedrijf?" Iemand die codes afgaat, leert
 * hiermee welke bedrijven bestaan — daarom staat er een grens per IP op de route en staat er
 * niets meer in dan een naam en een plaatje.
 */
/**
 * Het doorgerekende thema van een bedrijf (Stap 43).
 *
 * De ruwe kleuren **en** de varianten die de client nodig heeft. De varianten komen van de server en
 * niet uit de client, want de contrastdrempel is een regel van het product en geen voorkeur van een
 * browser (ADR-0056). Zou de client ze berekenen, dan zou een oudere app-versie een andere kleur
 * tonen dan een nieuwere.
 */
export const tenantThemeSchema = z.object({
  /** De kleur van de klant, onveranderd: zo verschijnt ze op een vlak. */
  primary: z.string(),
  /** Dezelfde kleur, zo nodig verdonkerd zodat ze als tekst op wit leesbaar is. */
  primaryText: z.string(),
  /** Wit of inkt: wat er bovenóp de primaire kleur hoort. */
  onPrimary: z.string(),
  accent: z.string(),
  /** De accentkleur, zo nodig verdonkerd zodat ze als dun lijntje zichtbaar is. */
  accentEdge: z.string(),
  onAccent: z.string(),
  /** Of dit het thema van de klant is, of dat van UP2D8 omdat de klant niets koos. */
  custom: z.boolean(),

  /*
    De organisatie zelf (Extra 1).

    Hoort hier en niet in een tweede endpoint: elk scherm dat de kleuren nodig heeft, heeft ook de
    naam en het logo nodig - de zijbalk, het aanmeldscherm, het voorbeeld. Twee aanroepen zouden
    betekenen dat de schil een halve huisstijl kan tonen.
  */

  /** De naam die de medewerker ziet. Nooit leeg: anders de officiele naam van het bedrijf. */
  displayName: z.string(),
  /** De officiele naam, die UP2D8 beheert. Staat erbij om het verschil te kunnen uitleggen. */
  legalName: z.string(),
  /** Ondertekende URL's, zestig seconden geldig; `null` als er niets geupload is. */
  logoUrl: z.string().url().nullable(),
  logoDarkUrl: z.string().url().nullable(),
  /**
   * Het pictogram in de maten die een beginscherm vraagt.
   *
   * Publieke, niet-ondertekende adressen: ze staan in het webmanifest, en dat wordt door het
   * besturingssysteem opgehaald - soms uren na de laatste aanmelding, wanneer een ondertekende
   * URL al lang verlopen is.
   */
  appIconUrls: z
    .object({
      size192: z.string(),
      size512: z.string(),
      maskable: z.string(),
    })
    .nullable(),

  /*
    Het volledige merk (MRK-1, ADR-0084): platformmerk, met daarover de huisstijl van het bedrijf.

    Optioneel in het schema, zodat een client die tegen een oudere server praat niet omvalt; de
    server vult ze altijd in. `custom` hierboven betekent sindsdien: de kleuren wijken af van die
    van UP2D8, door het platformmerk óf door het bedrijf. Of het bedrijf zelf koos, staat in `own`.
  */
  ...brandThemeFieldsSchema.partial().shape,

  /**
   * De afzendernaam van mails van dit bedrijf: de keuze van het bedrijf, anders die van het
   * platform, anders de productnaam. Alleen met een sessie; niet op het publieke aanmeldscherm.
   */
  emailSenderName: z.string().optional(),
  /**
   * Wat het bedrijf zelf koos, voor het formulier Huisstijl: `null` = erft. Alleen met een sessie.
   * De afbeeldingen staan hier niet in: die zijn er of niet, en dat zegt hun URL hierboven al.
   */
  own: brandStyleSettingsSchema.optional(),
  /**
   * Wat het bedrijf zou krijgen zonder eigen keuzes: het opgeloste platformmerk. Voor de
   * plaatshouders in het formulier en voor "Alles terug naar standaard". Alleen met een sessie.
   */
  inherited: brandStyleSettingsSchema.optional(),
  /** Of het bedrijf een eigen achtergrond voor het aanmeldscherm koos (de URL kan ook erven). */
  ownLoginBackground: z.boolean().optional(),
});
export type TenantTheme = z.infer<typeof tenantThemeSchema>;

/**
 * De huisstijl van het eigen bedrijf wijzigen.
 *
 * Elk veld: weggelaten = ongewijzigd, `null` = erven van het platformmerk. Primair en accent gaan
 * samen: geef je er één, geef dan ook de ander, of zet beide op `null`. "Alles terug naar
 * standaard" is elk veld op `null` (de afbeeldingen hebben hun eigen routes).
 *
 * Primair en accent waren verplicht vóór MRK-1; ze zijn optioneel geworden zodat een scherm dat
 * alleen het lettertype wijzigt, de kleuren niet hoeft mee te sturen. Een oudere client stuurt ze
 * altijd mee en merkt dus niets.
 */
export const updateTenantThemeRequestSchema = z.object({
  /** Leeg laten om terug te vallen op het platformmerk. */
  primary: z.string().trim().nullable().optional(),
  accent: z.string().trim().nullable().optional(),
  /**
   * De weergavenaam (Extra 1). Leeg of weggelaten betekent: gebruik de officiele naam.
   *
   * Dertig tekens, omdat de naam in een zijbalk van 240 px naast een logo staat. Langer zou daar
   * afgekapt worden, en dan kiest de klant een naam die hij nergens volledig terugziet.
   */
  displayName: z.string().trim().max(30).nullable().optional(),
  ...brandStyleRequestFields,
});
export type UpdateTenantThemeRequest = z.infer<typeof updateTenantThemeRequestSchema>;

export const tenantBrandingSchema = z.object({
  /** De officiele naam van het bedrijf. */
  name: z.string(),
  /** De naam die de klant koos, of `name` als hij niets koos (Extra 1). */
  displayName: z.string(),
  /** Een ondertekende URL, zestig seconden geldig; null als er geen logo is. */
  logoUrl: z.string().url().nullable(),
  /**
   * Het thema, zodat het aanmeldscherm al in de kleuren van de klant staat.
   *
   * Dit voegt niets toe aan wat deze publieke route al verklapte (een naam en een logo), en het is
   * precies waar de route voor bestaat: een medewerker die zijn bedrijfscode intypt, wil zien dat
   * hij bij zijn eigen bedrijf uitkomt.
   */
  theme: tenantThemeSchema,
  /**
   * Het type en de aanmeldwijze die daarbij hoort (ORG-3, ADR-0099): het aanmeldscherm van de app
   * begint bij e-mail of bij de pincode, en de teksten vóór het aanmelden volgen de woorden van het
   * type. Verklapt niets wat de naam niet al zegt. Optioneel voor een oudere server.
   */
  orgType: orgTypeSchema.optional(),
  loginDefault: loginDefaultSchema.optional(),
});
export type TenantBranding = z.infer<typeof tenantBrandingSchema>;

// --- Vestigingen (Stap 29) -----------------------------------------------------------

/**
 * Een vestiging van een klant.
 *
 * Eén tenant met sites in plaats van gekoppelde tenants (ADR-0073): zo is "alle vestigingen" de
 * standaard en "alleen Kortrijk" de uitzondering. Het oude platform deed het omgekeerd, en daardoor
 * kwam een bericht van de groep niet op de vloer van de dochter.
 */
export const siteSchema = z.object({
  id: z.guid(),
  /** Een korte code voor de mens: "GNT", "KTR". Staat op een scherm en in een export. */
  code: z.string(),
  name: z.string(),
  /**
   * `null` betekent: de tijdzone van het bedrijf.
   *
   * Geen tijdzone kopiëren bij het aanmaken: dan zou een bedrijf dat later verhuist, zijn sites
   * één voor één moeten bijwerken. Leeg is een verwijzing en geen ontbrekende waarde.
   */
  timezone: z.string().nullable(),
  address: z.string(),
  status: z.enum(['active', 'closed']),
});
export type Site = z.infer<typeof siteSchema>;

export const siteListSchema = z.object({ items: z.array(siteSchema) });
export type SiteList = z.infer<typeof siteListSchema>;

export const siteCodeSchema = z
  .string()
  .min(2)
  .max(12)
  .regex(/^[A-Z0-9-]+$/, 'Gebruik hoofdletters, cijfers of een streepje.');

export const createSiteRequestSchema = z.object({
  code: siteCodeSchema,
  name: z.string().min(2).max(120),
  /** Leeg laten betekent: de tijdzone van het bedrijf. */
  timezone: timezoneSchema.nullable().optional(),
  address: z.string().max(240).default(''),
});
export type CreateSiteRequest = z.infer<typeof createSiteRequestSchema>;

export const updateSiteRequestSchema = z.object({
  name: z.string().min(2).max(120).optional(),
  timezone: timezoneSchema.nullable().optional(),
  address: z.string().max(240).optional(),
  /**
   * Een vestiging sluiten en niet verwijderen.
   *
   * Een bericht dat vorig jaar naar Kortrijk ging, hoort te blijven verwijzen naar Kortrijk; een
   * verwijderde site zou dat bewijs onleesbaar maken. Een gesloten site verdwijnt uit de kiezers
   * en blijft in de geschiedenis.
   */
  status: z.enum(['active', 'closed']).optional(),
});
export type UpdateSiteRequest = z.infer<typeof updateSiteRequestSchema>;
